// Pure predicates and helpers over graph schemas — `GraphCustomType` field
// schemas and the database-level record schema (`asRecordType`,
// `schemaFields`) — split out of `@/components/graph/utils` so schema checks
// can be imported from low-level modules (e.g. `@/components/databases`)
// without dragging in the graph component tree (`utils` imports
// `component.ts`, which imports the graph `.vue` components).
import type {
  FieldOrigin,
  GraphCustomType,
  GraphDatabase,
  GraphDatabaseDictionary,
  GraphType,
} from "@ctrl/controller-rpc";

import { definitiveTableId, LABELS_TABLE_ID } from "@/components/tables";
import { arrayProperty, isRecord, property } from "@/utils/unknown";

export type RecordSchemaValue = { type: string };

export type RecordSchemaField = {
  name?: string;
  value?: RecordSchemaValue;
  origin?: FieldOrigin;
};

export type RecordSchema = {
  type: "record";
  fields: Record<string, RecordSchemaField>;
};

export type SchemaDatabaseSource = {
  type?: GraphDatabase["type"] | string | null;
};

export const asRecordSchemaValue = (
  value: unknown
): RecordSchemaValue | undefined => {
  if (!isRecord(value) || typeof value.type !== "string") return undefined;
  return { ...value, type: value.type };
};

const recordSchemaValueArray = (
  value: unknown,
  key: PropertyKey
): RecordSchemaValue[] | undefined => {
  const raw = arrayProperty(value, key);
  if (!raw) return undefined;
  const result: RecordSchemaValue[] = [];
  for (const item of raw) {
    const parsed = asRecordSchemaValue(item);
    if (!parsed) return undefined;
    result.push(parsed);
  }
  return result;
};

const parseRecordSchema = (value: unknown): RecordSchema | undefined => {
  if (!isRecord(value) || value.type !== "record" || !isRecord(value.fields)) {
    return undefined;
  }

  const fields: Record<string, RecordSchemaField> = {};
  for (const [id, field] of Object.entries(value.fields)) {
    if (!isRecord(field)) {
      fields[id] = {};
      continue;
    }
    const name = typeof field.name === "string" ? field.name : undefined;
    const schemaValue = asRecordSchemaValue(field.value);
    const origin =
      field.origin === "table" || field.origin === "virtual"
        ? field.origin
        : undefined;
    fields[id] = {
      ...(name !== undefined ? { name } : {}),
      ...(schemaValue ? { value: schemaValue } : {}),
      ...(origin ? { origin } : {}),
    };
  }
  return { type: "record", fields };
};

/**
 * Some RPC payloads (notably the `_routine.database` shipped with relation
 * cells) deliver `db.type` as a stringified JSON schema rather than the
 * structured `DatabaseType` declared by the typings. Embedded schemas can
 * legitimately omit field origins and may contain future type variants, so
 * this helper proves only the field metadata used by title resolution while
 * preserving each recognized value object's remaining data.
 */
export function asRecordType(
  type: GraphDatabase["type"] | string | null | undefined
): RecordSchema | undefined {
  if (!type) return undefined;
  if (typeof type === "string") {
    try {
      const parsed: unknown = JSON.parse(type);
      return parseRecordSchema(parsed);
    } catch {
      return undefined;
    }
  }
  return type.type === "record" ? type : undefined;
}

/**
 * The fields of a database schema as a list. Full controller databases carry
 * `FieldWithOrigin`; partial databases embedded in graph values can instead
 * carry an origin-less stringified schema, so callers only receive the common
 * metadata that has actually been proved.
 */
export function schemaFields(
  db: SchemaDatabaseSource | null | undefined
): RecordSchemaField[] {
  const fields = asRecordType(db?.type)?.fields;
  return fields ? Object.values(fields) : [];
}

/** Loose check: field value is `string` or `variant<string, null>` (optional string). */
export const isRecordSchemaTextField = (field: {
  value?: RecordSchemaValue;
}): boolean => {
  const v = field.value;
  if (!v) return false;
  if (v.type === "string") return true;
  const cases = recordSchemaValueArray(v, "cases");
  return (
    v.type === "variant" &&
    cases !== undefined &&
    cases.some((value) => value.type === "string") &&
    cases.some((value) => value.type === "null")
  );
};

/** Typed-schema version of {@link isRecordSchemaTextField}. */
export const isTextField = (field: { value: GraphCustomType }): boolean =>
  isRecordSchemaTextField(field);

/** Loose check: field value is `date` or `variant<date, null>` (optional date). */
export const isDateField = (field: { value?: RecordSchemaValue }): boolean => {
  const v = field.value;
  if (!v) return false;
  if (v.type === "date") return true;
  const cases = recordSchemaValueArray(v, "cases");
  return (
    v.type === "variant" &&
    cases !== undefined &&
    cases.some((value) => value.type === "date") &&
    cases.some((value) => value.type === "null")
  );
};

/**
 * Labels of a schema record's fields: both the map keys (often `column:…`
 * ids) and each field's display `name` when present.
 */
const recordFieldLabels = (value: RecordSchemaValue): string[] => {
  const fields = property(value, "fields");
  if (!isRecord(fields)) return [];
  const labels: string[] = [];
  for (const [id, field] of Object.entries(fields)) {
    labels.push(id);
    if (isRecord(field) && typeof field.name === "string") {
      labels.push(field.name);
    }
  }
  return labels;
};

/**
 * Calendar-date shaped schema values: the compact `{ type: "date" }` form,
 * date-shaped records (`Year`/`Month`/`Day`, by key or field name — including
 * the expanded form used by builtins like Planned For), and variants that
 * include either (e.g. Date|Week).
 */
const isDateShapedValue = (value: RecordSchemaValue): boolean => {
  if (value.type === "date") return true;
  if (value.type === "record") {
    const labels = new Set(
      recordFieldLabels(value).map((label) => label.toLowerCase())
    );
    return labels.has("year") && labels.has("month") && labels.has("day");
  }
  if (value.type === "variant") {
    const cases = recordSchemaValueArray(value, "cases");
    return cases?.some(isDateShapedValue) ?? false;
  }
  return false;
};

/**
 * Broader than {@link isDateField}: any field that can hold a calendar date,
 * including variants such as `Date | Week` (Tasks' "Planned For") and
 * controller schemas that expand dates to Year/Month/Day records instead of
 * the compact `{ type: "date" }` discriminant. Used for property icons
 * (`matchDefinition`), the Activity heatmap property picker, and
 * date-grouping checks.
 */
export const isDateShapedField = (field: {
  value?: RecordSchemaValue;
}): boolean => {
  const v = field.value;
  if (!v) return false;
  return isDateShapedValue(v);
};

export const isRelationType = (
  type: RecordSchemaValue | undefined
): boolean => {
  if (!type) return false;
  if (type.type === "relation") return true;
  const elements = asRecordSchemaValue(property(type, "elements"));
  return type.type === "list" && !!elements && isRelationType(elements);
};

/** Type is a single relation to the built-in Members table. */
export const isMemberRelationType = (
  type: RecordSchemaValue | undefined
): boolean =>
  !!type && type.type === "relation" && property(type, "table") === "member";

export const isNoteType = (type: RecordSchemaValue | undefined): boolean => {
  if (!type) return false;
  if (type.type === "note") return true;
  const elements = asRecordSchemaValue(property(type, "elements"));
  return type.type === "list" && !!elements && isNoteType(elements);
};

/** Field type is `note`, possibly wrapped in a variant (optional notes). */
export function isNotesPropertyType(
  type: RecordSchemaValue | undefined
): boolean {
  if (!type) return false;
  if (type.type === "note") return true;
  if (type.type === "variant") {
    const cases = recordSchemaValueArray(type, "cases");
    return cases?.some(isNotesPropertyType) ?? false;
  }
  return false;
}

/** Field type is `json`, possibly wrapped in a variant (optional json). */
export function isJsonPropertyType(
  type: RecordSchemaValue | undefined
): boolean {
  if (!type) return false;
  if (type.type === "json") return true;
  if (type.type === "variant") {
    const cases = recordSchemaValueArray(type, "cases");
    return cases?.some(isJsonPropertyType) ?? false;
  }
  return false;
}

/**
 * Peel GraphField `{ type: T }` wrappers and match a `const "notes"` hint
 * (`GraphNotesTypeTag` in properties.ts).
 */
const isNotesTypeHint = (type: unknown): boolean => {
  if (!isRecord(type)) return false;
  const schema = asRecordSchemaValue(type);
  if (schema && selectCaseValue(schema) === "notes") return true;
  if (schema?.type === "record") {
    const fields = property(type, "fields");
    return isRecord(fields) && isNotesTypeHint(property(fields, "type"));
  }
  const inner = property(type, "type");
  return inner !== undefined && inner !== type ? isNotesTypeHint(inner) : false;
};

/**
 * Whether a query column GraphType is notes: schema `{ type: "note" }`, a
 * `blocks`/`Blocks` record, or a `_routine` const `"notes"` type hint.
 */
export function isNotesGraphType(type: GraphType | GraphCustomType): boolean {
  if (isNotesPropertyType(asRecordSchemaValue(type))) return true;
  if (type.type === "variant") {
    return type.cases.some((entry) => isNotesGraphType(entry));
  }
  if (type.type !== "record") return false;
  const fields = type.fields;
  if ("blocks" in fields || "Blocks" in fields) return true;
  const routine = property(fields, "_routine");
  const routineType = isRecord(routine)
    ? (property(routine, "type") ?? routine)
    : routine;
  return isNotesTypeHint(routineType);
}

/**
 * Whether a column type can hold a calendar date. Same rules as
 * {@link isDateShapedField}, applied to query `available_columns` GraphTypes
 * (where the controller erases `date` into Year/Month/Day records).
 */
export function isDateShapedGraphType(
  type: GraphType | GraphCustomType
): boolean {
  return isDateShapedField({ value: asRecordSchemaValue(type) });
}

/**
 * Strip a top-level optional wrapper (`variant<..., null>`), returning the inner
 * type and whether a null case was present.
 */
function stripNullVariant(type: GraphCustomType): {
  inner: GraphCustomType;
  optional: boolean;
} {
  if (type.type === "variant") {
    const nonNull = type.cases.filter((c) => c.type !== "null");
    const hadNull = nonNull.length !== type.cases.length;
    if (hadNull) {
      if (nonNull.length === 1) return { inner: nonNull[0], optional: true };
      return { inner: { type: "variant", cases: nonNull }, optional: true };
    }
  }
  return { inner: type, optional: false };
}

/**
 * Peel optional and list wrappers off a property type, exposing the core type
 * used for value enumeration. Optionality is derived separately (see
 * `introspectType` in `@/components/graph/introspection`) so it stays
 * consistent with the schema editor.
 */
export function unwrap(type: GraphCustomType): {
  core: GraphCustomType;
  list: boolean;
} {
  let core = stripNullVariant(type).inner;
  let list = false;
  if (core.type === "list") {
    list = true;
    core = stripNullVariant(core.elements).inner;
  }
  return { core, list };
}

function stripNullRecordSchemaVariant(
  type: RecordSchemaValue
): RecordSchemaValue {
  if (type.type !== "variant") return type;
  const cases = recordSchemaValueArray(type, "cases");
  if (!cases) return type;
  const nonNull = cases.filter((value) => value.type !== "null");
  const hadNull = nonNull.length !== cases.length;
  return hadNull && nonNull.length === 1 ? nonNull[0] : type;
}

function unwrapRecordSchemaValue(type: RecordSchemaValue): {
  core: RecordSchemaValue;
  list: boolean;
} {
  const core = stripNullRecordSchemaVariant(type);
  if (core.type !== "list") return { core, list: false };
  const elements = asRecordSchemaValue(property(core, "elements"));
  return {
    core: elements ? stripNullRecordSchemaVariant(elements) : core,
    list: true,
  };
}

/** Extract the string literal of a select case (`string_const` or string `const`). */
export function selectCaseValue(type: RecordSchemaValue): string | undefined {
  const value = property(type, "value");
  if (type.type === "string_const" && typeof value === "string") return value;
  if (type.type === "const" && typeof value === "string") return value;
  return undefined;
}

/** Option values of a select core type, or `undefined` when it is not a select. */
function selectCaseValues(core: RecordSchemaValue): string[] | undefined {
  const cases =
    core.type === "variant" ? recordSchemaValueArray(core, "cases") : [core];
  if (!cases) return undefined;
  const values = cases.map(selectCaseValue);
  if (values.some((value) => value === undefined)) return undefined;
  return values.filter((value): value is string => value !== undefined);
}

/**
 * The option values of a select field — a (possibly optional) variant whose
 * cases are all string constants (see `SelectDefinition.vue`) — or
 * `undefined` when the field is not a (single) select.
 */
export function selectFieldValues(field: {
  value?: RecordSchemaValue;
}): string[] | undefined {
  if (!field.value) return undefined;
  const { core, list } = unwrapRecordSchemaValue(field.value);
  if (list) return undefined;
  return selectCaseValues(core);
}

/** Whether the field is a select (see {@link selectFieldValues}). */
export function isSelectField(field: { value?: RecordSchemaValue }): boolean {
  return selectFieldValues(field) !== undefined;
}

/**
 * Whether the field is a multi-select: a list of string-constant select
 * cases, possibly optional.
 */
export function isMultiSelectField(field: {
  value?: RecordSchemaValue;
}): boolean {
  if (!field.value) return false;
  const { core, list } = unwrapRecordSchemaValue(field.value);
  return list && selectCaseValues(core) !== undefined;
}

/**
 * Field is a relation to the Labels database, including optional and
 * list-wrapped forms (the built-in Labels property is typically multi-value).
 */
export function isLabelsRelationField(field: {
  value?: RecordSchemaValue;
}): boolean {
  if (!field.value) return false;
  const { core } = unwrapRecordSchemaValue(field.value);
  if (core.type !== "relation") return false;
  const table = property(core, "table");
  return (
    typeof table === "string" && definitiveTableId(table) === LABELS_TABLE_ID
  );
}

export const relationTargetsFromType = (
  type: RecordSchemaValue | undefined
): string[] => {
  if (!type) return [];
  switch (type.type) {
    case "relation": {
      const table = property(type, "table");
      return typeof table === "string" ? [table] : [];
    }
    case "list": {
      const elements = asRecordSchemaValue(property(type, "elements"));
      return elements ? relationTargetsFromType(elements) : [];
    }
    case "tuple":
    case "variant": {
      const values = recordSchemaValueArray(
        type,
        type.type === "tuple" ? "elements" : "cases"
      );
      return values?.flatMap(relationTargetsFromType) ?? [];
    }
    case "record": {
      const fields = property(type, "fields");
      if (!isRecord(fields)) return [];
      return Object.values(fields).flatMap((field) => {
        const value = isRecord(field)
          ? asRecordSchemaValue(field.value)
          : undefined;
        return value ? relationTargetsFromType(value) : [];
      });
    }
    default:
      return [];
  }
};

/** A type in `types`, by canonical id or an alias the controller still uses. */
export function databaseForTable(
  types: GraphDatabaseDictionary | undefined,
  tableId: string
): GraphDatabase | undefined {
  if (!types) return undefined;
  const direct = types[tableId];
  if (direct) return direct;
  return Object.values(types).find(
    (database) => definitiveTableId(database.id) === tableId
  );
}

/**
 * The database a relation field points at, when the field is a relation
 * (possibly optional or list-wrapped) to a single type present in `types`.
 */
export function relationTargetDatabase(
  types: GraphDatabaseDictionary | undefined,
  field: { value?: RecordSchemaValue }
): GraphDatabase | undefined {
  const targets = relationTargetsFromType(field.value);
  if (targets.length !== 1) return undefined;
  return databaseForTable(types, definitiveTableId(targets[0]));
}
